import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, ShieldCheck, Zap, Star, Layout, Briefcase } from "lucide-react";

const FranquiaIptvPage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Franquia IPTV Profissional", href: "/blog/franquia-iptv-profissional" }
  ];

  const sections = [
    {
      title: "O Que Você Recebe ao Abrir Sua Franquia?",
      type: "grid" as const,
      content: (
        <>
          {[
            { 
              t: "Painel IPTV Profissional", 
              d: "Gestão de clientes, controle de vencimentos, criação de testes, gestão de créditos, controle de revendedores e relatórios administrativos.", 
              i: <Layout className="w-6 h-6" /> 
            },
            { 
              t: "Sua Própria Marca", 
              d: "Logo personalizada, nome da sua operação e aparência profissional para fortalecer sua marca no mercado.", 
              i: <Star className="w-6 h-6" /> 
            },
            { 
              t: "Tabela de Preços", 
              d: "Liberdade total para definir seus preços, maior flexibilidade e controle sobre sua lucratividade.", 
              i: <Briefcase className="w-6 h-6" /> 
            }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-3xl bg-white/[0.02] border border-white/5">
              <div className="w-12 h-12 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6">
                {item.i}
              </div>
              <h3 className="text-xl font-bold mb-3">{item.t}</h3>
              <p className="text-muted-foreground text-sm">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Servidores Utilizados e Apps",
      type: "featured" as const,
      content: (
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold mb-6 italic italic uppercase tracking-tighter">Servidores Premium</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-electric mt-1 shrink-0" />
                <div>
                  <span className="font-bold">Prime Play:</span> Estrutura preparada para oferecer estabilidade e gerenciamento eficiente.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-electric mt-1 shrink-0" />
                <div>
                  <span className="font-bold">P2Pro:</span> Solução desenvolvida para operações que buscam escalabilidade e crescimento.
                </div>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-6 italic italic uppercase tracking-tighter">Aplicativos Disponíveis</h3>
            <div className="grid grid-cols-2 gap-4">
              {["One X", "RP 725", "Fun Play", "VUser", "Blessed", "Lazer Play"].map((app) => (
                <div key={app} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-center font-bold text-sm">
                  {app}
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Painel IPTV POS PAGO"
      title="FRANQUIA IPTV PROFISSIONAL"
      subtitle="Abra Sua Própria Operação IPTV com Estrutura Completa e Marca Própria"
      heroImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Construa seu próprio negócio utilizando uma estrutura profissional, servidores premium, painel personalizado, aplicativos parceiros e suporte especializado.",
        "Com a nossa franquia IPTV, você não precisa se preocupar com infraestrutura complexa. Você recebe uma operação pronta para crescer, focando apenas em conquistar clientes e expandir seus resultados.",
        "Nossa franquia oferece acesso à infraestrutura dos servidores Prime Play e P2Pro, permitindo que você trabalhe com sua própria identidade visual, sua própria estratégia comercial e uma estrutura preparada para crescimento."
      ]}
      benefits={[
        { title: "Painel Personalizado", description: "Gerencie toda sua operação através de um painel intuitivo e completo.", icon: <Layout /> },
        { title: "Logo Própria", description: "Destaque-se no mercado com uma identidade própria e fortalecimento da sua marca.", icon: <Star /> },
        { title: "Tabela de Preços", description: "Liberdade total para definir seus preços e estratégias comerciais.", icon: <Briefcase /> },
        { title: "Apps Inclusos", description: "Sua franquia já inicia preparada para trabalhar com aplicativos parceiros.", icon: <Zap /> },
        { title: "Suporte Especializado", description: "Equipe disponível para auxiliar durante sua jornada empreendedora.", icon: <ShieldCheck /> },
        { title: "Escalabilidade", description: "Ambiente preparado para crescimento contínuo conforme sua carteira cresce.", icon: <Check /> }
      ]}
      howItWorks={[
        { title: "Apresentação", description: "Solicite uma apresentação para conhecer as condições e opções disponíveis." },
        { title: "Personalização", description: "Configure sua marca, logo e identidade visual da sua operação." },
        { title: "Recebimento", description: "Acesse uma estrutura profissional completa pronta para utilização imediata." },
        { title: "Crescimento", description: "Cadastre seus primeiros clientes e comece a desenvolver sua carteira." }
      ]}
      diferenciais={[
        { title: "Marca Própria", description: "Construa autoridade utilizando sua própria identidade visual." },
        { title: "Sistema Completo", description: "Ferramentas que facilitam o gerenciamento da operação." },
        { title: "Atualizações", description: "Melhorias contínuas para manter a estrutura sempre moderna." }
      ]}
      faq={[
        { question: "Qual o valor da franquia?", answer: "Os valores são consultivos. Entre em contato para conhecer as opções disponíveis de acordo com seu objetivo." },
        { question: "Recebo minha própria marca?", answer: "Sim. Você pode utilizar sua própria identidade visual e o painel pode ser configurado com sua logo." },
        { question: "Posso criar meus próprios preços?", answer: "Sim. Você possui liberdade comercial para definir sua tabela de preços e estratégias de lucro." },
        { question: "Os aplicativos já estão inclusos?", answer: "A estrutura é compatível com os aplicativos parceiros informados (One X, RP 725, Fun Play, etc)." },
        { question: "Preciso ter experiência?", answer: "Não. A estrutura foi desenvolvida para facilitar sua operação e oferecemos todo o suporte especializado necessário." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Solicitar Apresentação Agora"
      ctaDescription="Fale com um especialista e conheça as condições para abrir sua franquia IPTV."
      ctaButtonText="Falar no WhatsApp"
    />
  );
};

export const Route = createFileRoute("/blog/franquia-iptv-profissional")({
  head: () => ({
    meta: [
      { title: "Franquia IPTV Profissional - Tenha Sua Própria Marca | P2 PRO" },
      { name: "description", content: "Abra sua própria operação IPTV com marca própria, painel personalizado e servidores premium. Estrutura completa para empreendedores." }
    ],
  }),
  component: FranquiaIptvPage,
});
